import { AsyncPipe, NgIf } from '@angular/common';
import { Component, HostBinding } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Fixture, FixturePage } from '@cds/betting-offer';
import { BetBuilderFixture } from '@cds/betting-offer/domain-specific/bet-builder';
import { BetBuilderConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { HooksWireup, OnRouteResolve } from '@frontend/sports/common/core/utils/hooks';
import { ObservableGrid, ObservableGridFactory } from '@frontend/sports/grid/core/feature/observables';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { finalize } from 'rxjs';

import { BetBuilderButtonComponent } from '../../../betbuilder/components/button/betbuilder-button.component';
import { BetBuilderButtonSource } from '../../../betbuilder/model/bet-builder.model';
import { BetBuilderConfigService } from '../../../betbuilder/services/betbuilder-config.service';
import { BetBuilderService } from '../../../betbuilder/services/betbuilder.service';
import { EpcotConfigService } from '../../../common/epcot-config.service';
import { ErrorComponent } from '../../../common/error/error.component';
import { GridModule } from '../../../grid/grid.module';
import { EventLinkConfig, MarketUrlParam } from '../../../navigation-core/url-helper.service';
import { BetBuilderDetail } from './bet-builder-detail.model';
import { PromotedBetBuilderComponent } from './promoted-betbuilder.component';

@HooksWireup()
@Component({
    templateUrl: './betbuilder-event-list.component.html',
    selector: 'ms-betbuilder-event-list',
    providers: [ObservableGridFactory],
    imports: [AsyncPipe, NgIf, ErrorComponent, PromotedBetBuilderComponent, GridModule, BetBuilderButtonComponent],
})
export class BetBuilderEventListComponent extends OnRouteResolve<BetBuilderDetail> {
    @HostBinding('class') className = 'betbuilder-event-list';

    loading: boolean;
    grid: ObservableGrid;
    gridLinkConfig: EventLinkConfig;

    trackingSource: string;
    promotedMarkets: BetBuilderFixture[];
    betBuilderEpcotEnabled = false;

    buttonSource = BetBuilderButtonSource.Coupons;

    private skip: number;
    private fixturesCount: number;
    private groupThreshold: number;

    constructor(
        route: ActivatedRoute,
        public sitecore: Sitecore,
        public betBuilderConfig: BetBuilderConfig,
        private betBuilderService: BetBuilderService,
        private gridFactory: ObservableGridFactory,
        private trackingService: TrackingService,
        private epcotConfigService: EpcotConfigService,
        private betBuilderConfigService: BetBuilderConfigService,
    ) {
        super(route);

        this.skip = this.betBuilderConfig.pageSize;
        this.betBuilderEpcotEnabled = this.epcotConfigService.isEnabled();
    }

    onRouteResolved(model: BetBuilderDetail): void {
        if (this.fixturesCount || !model) {
            return;
        }

        this.promotedMarkets = model.tiles;
        this.setEventsModels(model.fixture);
    }

    onScrollAtBottom(): void {
        if (!this.hasMoreFixtures || this.loading) {
            return;
        }

        const { sport } = this.route.snapshot.params;

        this.loading = true;

        this.betBuilderService
            .getFixtures(sport, this.skip, this.betBuilderConfig.pageSize)
            .pipe(finalize(() => (this.loading = false)))
            .subscribe((fixturePage) => {
                if (!fixturePage) {
                    return;
                }

                this.skip += this.betBuilderConfig.pageSize;

                if (fixturePage.fixtures && fixturePage.fixtures.length > 0) {
                    this.addEvents(fixturePage.fixtures);
                }
            });
    }

    eventClicked(): void {
        this.trackingService.update({ [trackingConstants.PAGE_REFERRING_ACTION]: 'BuildABet_CouponPage_Link_Click' });
    }

    get hasMoreFixtures(): boolean {
        return this.fixturesCount > this.skip;
    }

    private addEvents(fixtuerPage: Fixture[]): void {
        const visibleFixtures = fixtuerPage.filter((f) => this.filterVisibleFixtures(f));
        this.grid.addEvents(visibleFixtures);
    }

    private setEventsModels(fixturePage: FixturePage): void {
        if (!fixturePage?.fixtures || fixturePage.fixtures.length === 0) {
            return;
        }

        this.fixturesCount = fixturePage.totalCount;
        this.groupThreshold = this.betBuilderConfig.pageSize * 3;
        this.trackingSource = 'Coupons/BetBuilder';
        this.gridLinkConfig = {
            extraParams: MarketUrlParam.BetBuilder,
            emptyText: this.sitecore.betBuilder.messages?.EmptyMarketLinkText ?? '',
        };

        const visibleFixtures = fixturePage.fixtures.filter((f) => this.filterVisibleFixtures(f));

        this.grid = this.gridFactory.fromResponse(`${fixturePage.fixtures[0].sport.id}/coupons/bet-builder`, visibleFixtures, {
            collapsedThreshold: this.groupThreshold,
        });
    }

    private filterVisibleFixtures(fixture: Fixture): boolean {
        if (!this.betBuilderConfigService.useBetBuilderInEntainUI(fixture.sport.id)) {
            return true;
        }

        return !!fixture?.addons?.betBuilderFixture && !!fixture?.addons?.sportcastId;
    }
}
